// @ts-check
import { useState } from "react"
import { Check, X, ChevronDown, ChevronUp } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const LARGE_REWRITE_THRESHOLD = 0.65

/**
 * @param {{ old_string: string, new_string: string }} params
 */
function isLargeRewrite({ old_string, new_string }) {
  if (!old_string) return true // full rewrites always use before/after
  const delta = Math.abs(new_string.length - old_string.length)
  return delta / old_string.length > LARGE_REWRITE_THRESHOLD
}

/**
 * @param {{
 *   diff: {
 *     id: string,
 *     isFullRewrite?: boolean,
 *     old_string?: string,
 *     new_string: string,
 *     rationale: string,
 *     status: "pending" | "stale",
 *   },
 *   onAccept: (diffId: string) => void,
 *   onReject: (diffId: string) => void,
 *   isLoading?: boolean,
 * }} props
 */
export default function DiffHunk({ diff, onAccept, onReject, isLoading = false }) {
  const [beforeAfterOpen, setBeforeAfterOpen] = useState(false)
  const useToggle = diff.isFullRewrite || isLargeRewrite({ old_string: diff.old_string ?? "", new_string: diff.new_string })

  if (diff.status === "stale") {
    return (
      <div className="my-2 rounded-md border border-dashed border-yellow-400/60 bg-yellow-50/30 dark:bg-yellow-900/10 px-3 py-2 text-xs text-yellow-700 dark:text-yellow-400">
        This suggestion is no longer applicable to the current document.
        <Button variant="ghost" size="sm" className="ml-2 h-5 px-1.5 text-xs" onClick={() => onReject(diff.id)}>
          Dismiss
        </Button>
      </div>
    )
  }

  return (
    <div className="my-3 rounded-lg border border-border bg-muted/30 overflow-hidden">
      {/* Rationale */}
      {diff.rationale && (
        <div className="px-3 pt-2 pb-1 text-xs text-muted-foreground italic border-b border-border/50">
          {diff.rationale}
        </div>
      )}

      {/* Diff content */}
      <div className="px-3 py-2">
        {useToggle ? (
          <BeforeAfterToggle
            old_string={diff.old_string}
            new_string={diff.new_string}
            isFullRewrite={diff.isFullRewrite}
            open={beforeAfterOpen}
            onToggle={() => setBeforeAfterOpen(v => !v)}
          />
        ) : (
          <InlineRedlines old_string={diff.old_string ?? ""} new_string={diff.new_string} />
        )}
      </div>

      {/* Actions */}
      <div data-diff-action className="flex items-center gap-2 px-3 pb-3 pt-1">
        <Button
          size="sm"
          variant="default"
          className="h-7 gap-1 bg-green-600 hover:bg-green-700 text-white"
          onClick={() => onAccept(diff.id)}
          disabled={isLoading}
        >
          <Check size={13} />
          Accept
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 text-destructive border-destructive/40 hover:bg-destructive/10"
          onClick={() => onReject(diff.id)}
          disabled={isLoading}
        >
          <X size={13} />
          Reject
        </Button>
      </div>
    </div>
  )
}

/**
 * @param {{ old_string: string, new_string: string }} props
 */
function InlineRedlines({ old_string, new_string }) {
  return (
    <div className="font-mono text-sm leading-relaxed whitespace-pre-wrap">
      {old_string && (
        <span className="line-through text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 rounded px-0.5">
          {old_string}
        </span>
      )}
      {old_string && new_string && " "}
      <span className="text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/30 rounded px-0.5">
        {new_string}
      </span>
    </div>
  )
}

/**
 * @param {{ old_string?: string, new_string: string, isFullRewrite?: boolean, open: boolean, onToggle: () => void }} props
 */
function BeforeAfterToggle({ old_string, new_string, isFullRewrite, open, onToggle }) {
  const label = isFullRewrite ? "AI rewrote this document" : "AI rewrote this section"

  return (
    <div>
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        {label} — click to compare
      </button>

      {open && (
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs font-mono">
          <div className="rounded bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-2 overflow-auto max-h-48">
            <div className="text-red-700 dark:text-red-400 font-sans font-semibold mb-1 text-[10px] uppercase tracking-wide">Before</div>
            <span className="whitespace-pre-wrap text-red-800 dark:text-red-300">{old_string ?? "(empty)"}</span>
          </div>
          <div className="rounded bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 p-2 overflow-auto max-h-48">
            <div className="text-green-700 dark:text-green-400 font-sans font-semibold mb-1 text-[10px] uppercase tracking-wide">After</div>
            <span className="whitespace-pre-wrap text-green-800 dark:text-green-300">{new_string}</span>
          </div>
        </div>
      )}
    </div>
  )
}
